<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onUnmounted, ref } from "vue"
import { useEventBus } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { LAYOUT_SIDEBAR_EXPANDED_EVENT } from "@core/layouts/layout.events"
import { SUPPORT_OPEN_EVENT } from "./support.events"
import { useResponsive } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            support: {
                buttonLabel: "Aide",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Masque le bouton tout en gardant la modal disponible (ex. ouverture programmatique).
     */
    hideButton?: boolean
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    hideButton: false,
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const { isMobile } = useResponsive()

// ================================
// STATE
// ================================
/**
 * Contrôle l'affichage de la modal d'aide.
 */
const visible = ref(false)

/**
 * Sidebar layout développée : z-index du bouton sous le calque `z-1` de l’aside.
 */
const isSidebarExpanded = ref(false)

// ================================
// COMPUTED
// ================================
/**
 * Indique si le bouton d'aide doit être affiché (employé avec droits d'écriture et client Homeys).
 */
const getCanShowButton = computed(() => {
    return authStore.isEmployeWriteOrHigher && authStore.isClientHomeys
})

// ================================
// INTERACTIONS
// ================================
/**
 * Ouvre la modal d'aide au clic sur le bouton.
 */
function onClickOpenSupport(): void {
    visible.value = true
}

// ================================
// LIFECYCLE
// ================================
const busSupportOpen = useEventBus<void>(SUPPORT_OPEN_EVENT)
const busSidebarExpanded = useEventBus<boolean>(LAYOUT_SIDEBAR_EXPANDED_EVENT)

const stopSupportOpenListener = busSupportOpen.on(() => {
    visible.value = true
})
const stopSidebarExpandedListener = busSidebarExpanded.on((expanded) => {
    isSidebarExpanded.value = expanded
})

onUnmounted(() => {
    stopSupportOpenListener()
    stopSidebarExpandedListener()
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div
        class="support-button fixed bottom-8 right-8"
        :class="isSidebarExpanded && isMobile ? 'z-0' : 'z-[2000]'"
    >
        <BaseButton
            v-if="!hideButton && getCanShowButton"
            icon-pos="left"
            :label="t('support.buttonLabel')"
            rounded
            @click="onClickOpenSupport"
        >
            <template #icon>
                <BaseIcon icon="far fa-circle-question" class="text-base" />
            </template>
        </BaseButton>
        <SupportModal v-model:visible="visible" />
    </div>
</template>
